/**
 * AuthShell — shared chrome for all public/auth pages.
 *
 * Provides:
 *  • Sticky navy header with brand + language toggle + optional right-side slot
 *  • Main content area (flex-grows to fill viewport)
 *  • Simple navy footer with translated copyright
 *
 * Usage:
 *   <AuthShell rightContent={<Button ...>Sign Up</Button>}>
 *     {page body}
 *   </AuthShell>
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { AppBar, Toolbar, Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLanguage } from "../context/LanguageContext";
import logo from "url:../../assets/logo.png";

const NAVY   = "#0F2156";

interface AuthShellProps {
  children: React.ReactNode;
  /** Content placed on the right side of the header (e.g. login/signup buttons). */
  rightContent?: React.ReactNode;
}

export default function AuthShell({ children, rightContent }: AuthShellProps) {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* ── Sticky header ── */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: NAVY,
          borderBottom: `1px solid ${alpha("#fff", 0.08)}`,
          boxShadow: `0 2px 16px ${alpha(NAVY, 0.4)}`,
        }}
      >
        <Toolbar sx={{ minHeight: 56, gap: 1 }}>
          {/* Brand logo — clicking goes to landing */}
          <Box
            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={() => navigate("/")}
          >
            <Box
              component="img"
              src={logo}
              alt={t("common.appName")}
              sx={{ height: 40, width: "auto", mixBlendMode: "screen" }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Language toggle — always "English" or "नेपाली" */}
          <Button
            size="small"
            variant="text"
            onClick={() => setLanguage(language === "ne" ? "en" : "ne")}
            sx={{
              color: alpha("#fff", 0.8),
              fontSize: "0.82rem",
              fontWeight: 500,
              textTransform: "none",
              px: 1,
              "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            {language === "ne" ? "English" : "नेपाली"}
          </Button>

          {/* Optional right-side slot */}
          {rightContent}
        </Toolbar>
      </AppBar>

      {/* ── Page content ── */}
      <Box sx={{ flex: 1 }}>
        {children}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          bgcolor: NAVY,
          borderTop: `1px solid ${alpha("#fff", 0.07)}`,
          color: alpha("#fff", 0.5),
          textAlign: "center",
          py: 2.5,
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.78rem" }}>
          {t("landing.footer.copyright")}
        </Typography>
      </Box>
    </Box>
  );
}
